import React, { useState, useEffect, useRef } from 'react';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import { BROWSER_STORAGE_ITEMS } from '../../../utils/enums';
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 300,
    alignItems: 'center',
    marginTop: 20,
  },
  input: {
    width: 300,
    '& + &': {
      marginTop: 20,
    },
  },
  helperText: {
    margin: '3px 0 0 3px',
    padding: 0,
    lineHeight: '16px',
  },
}));

export const initialState = {
  name_on_account: '',
  account_number: '',
  routing_number: '',
  account_type: 'PERSONAL_CHECKING',
};

export default function CollectACHDetailsFormEmbeddable({
  onChange,
  onValidationChange,
  enableBrowserCache,
  browserCacheKey = BROWSER_STORAGE_ITEMS.ACH_DETAILS_FORM_VALUES,
}) {
  const classes = useStyles();

  // state variables
  const [nameOnAccount, setNameOnAccount] = useState(
    initialState.name_on_account
  );
  const [nameOnAccountError, setNameOnAccountError] = useState(false);
  const [accountNumber, setAccountNumber] = useState(
    initialState.account_number
  );
  const [accountNumberError, setAccountNumberError] = useState(false);
  const [routingNumber, setRoutingNumber] = useState(
    initialState.routing_number
  );
  const [routingNumberError, setRoutingNumberError] = useState(false);
  const [accountType, setAccountType] = useState(initialState.account_type);

  // refs
  const nameOnAccountRef = useRef(null);
  const accountNumberRef = useRef(null);
  const routingNumberRef = useRef(null);

  // run once on component mount, initialize form with cached values from session storage
  useEffect(() => {
    let cachedValues;
    if (enableBrowserCache) {
      cachedValues = JSON.parse(window.sessionStorage.getItem(browserCacheKey));
    }

    if (cachedValues) {
      setNameOnAccount(cachedValues.name_on_account);
      setAccountNumber(cachedValues.account_number);
      setRoutingNumber(cachedValues.routing_number);
      setAccountType(cachedValues.account_type);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // run every time a form value changes
  useEffect(
    () => {
      const newValues = {
        name_on_account: nameOnAccount,
        account_number: accountNumber,
        routing_number: routingNumber,
        account_type: accountType,
      };

      // update cached values in session storage
      if (enableBrowserCache) {
        window.sessionStorage.setItem(
          browserCacheKey,
          JSON.stringify(newValues)
        );
      }

      // update parent component
      onChange(newValues);

      onValidationChange(
        nameOnAccount.length > 0 &&
          accountNumber.length > 0 &&
          validateAccountNumber(accountNumber) && // numeric between 8 and 17 digits
          routingNumber.length > 0 &&
          validateRoutingNumber(routingNumber) // numeric exactly 9 digits
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      nameOnAccount,
      accountNumber,
      routingNumber,
      accountType,
      enableBrowserCache,
      browserCacheKey,
    ]
  );

  const validateAccountNumber = (value) => {
    return /^[a-zA-Z0-9]{8,17}$/.test(value);
  };

  const validateRoutingNumber = (value) => {
    return /^\d{9}$/.test(value);
  };

  return (
    <>
      <div className={classes.root}>
        <TextField
          autoFocus
          className={classes.input}
          inputProps={{
            'data-lpignore': 'true',
            autoFocus: true,
          }}
          FormHelperTextProps={{
            classes: {
              root: classes.helperText,
            },
          }}
          inputRef={nameOnAccountRef}
          value={nameOnAccount}
          variant="outlined"
          size="small"
          onChange={(evt) => {
            setNameOnAccount(evt.target.value);
            setNameOnAccountError(evt.target.value === '');
          }}
          onBlur={() => {
            setNameOnAccountError(nameOnAccount.length === 0);
          }}
          label="Name on Account"
          error={nameOnAccountError}
          helperText={nameOnAccountError ? 'Name on account is required' : ''}
          required
        />

        <TextField
          className={classes.input}
          inputProps={{
            'data-lpignore': 'true',
            minLength: 8,
            maxLength: 17,
          }}
          FormHelperTextProps={{
            classes: {
              root: classes.helperText,
            },
          }}
          inputRef={accountNumberRef}
          value={accountNumber}
          variant="outlined"
          size="small"
          onChange={(evt) => {
            const value = evt.target.value
              .replace(/[a-z]/g, (match) => match.toUpperCase()) // capitalize all lowercase letters
              .replace(/[^a-zA-Z0-9]$/g, ''); // disable typing non-numeric characters
            setAccountNumber(value);
            setAccountNumberError(value.length === 0);
          }}
          onBlur={() => {
            setAccountNumberError(!validateAccountNumber(accountNumber));
          }}
          label="Account Number"
          error={accountNumberError}
          helperText={
            accountNumberError && accountNumber.length === 0
              ? 'Account number is required'
              : accountNumber.length > 0 &&
                  !validateAccountNumber(accountNumber)
                ? 'Account number must be between 8 and 17 alphanumeric characters'
                : ''
          }
          required
        />
        <TextField
          className={classes.input}
          style={{ marginBottom: 20 }}
          inputProps={{
            'data-lpignore': 'true',
            maxLength: 9,
          }}
          FormHelperTextProps={{
            classes: {
              root: classes.helperText,
            },
          }}
          inputRef={routingNumberRef}
          value={routingNumber}
          variant="outlined"
          size="small"
          onChange={(evt) => {
            const value = evt.target.value.replace(/[^0-9]/g, ''); // disable typing non-numeric characters
            setRoutingNumber(value);
            setRoutingNumberError(value.length === 0);
          }}
          onBlur={(evt) => {
            setRoutingNumberError(!validateRoutingNumber(routingNumber));
          }}
          label="Routing Number"
          error={routingNumberError}
          helperText={
            routingNumberError && routingNumber.length === 0
              ? 'Routing number is required'
              : routingNumber.length > 0 &&
                  !validateRoutingNumber(routingNumber)
                ? 'Routing number must be exactly 9 digits'
                : ''
          }
          required
        />
        <FormControl
          variant="outlined"
          className={classes.formControl}
          size="small"
          fullWidth
        >
          <InputLabel id="account-type-label">Account Type</InputLabel>
          <Select
            className={classes.input}
            style={{ marginBottom: 20 }}
            labelId="account-type-label"
            id="account-type-select"
            value={accountType}
            label="Account Type"
            onChange={(evt) => setAccountType(evt.target.value)}
            displayEmpty
          >
            <MenuItem value="PERSONAL_CHECKING">Personal Checking</MenuItem>
            <MenuItem value="PERSONAL_SAVINGS">Personal Savings</MenuItem>
          </Select>
        </FormControl>
      </div>
    </>
  );
}
